/**
 * Equipment Service
 * 
 * Service for fetching equipment hierarchy data with caching mechanisms
 * for improved performance.
 */

import { apiClient } from '../api';
import { EquipmentHierarchy, EquipmentNode, Equipment } from './types';
// Cache configuration
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
// Cache duration: 1 day in milliseconds
const CACHE_TTL_EQUIPMENT_TREE = 24 * 60 * 60 * 1000;
const CACHE_EQUIPMENT_TREE_KEY_PREFIX = "full_eq_tree_";

// Cache structure
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// In-memory cache
const equipmentCache: Record<string, CacheEntry<EquipmentHierarchy>> = {};

/**
 * Generates a cache key for equipment hierarchy
 */
const getCacheKey = (site: string, apmId: string): string => {
  return `equipment_${site}_${apmId}`;
};

/**
 * Generates a cache key for full equipment tree (site only)
 */
const getFullTreeCacheKey = (site: string): string => {
  return `${CACHE_EQUIPMENT_TREE_KEY_PREFIX}${site}`;
};

/**
 * Checks if a cache entry is still valid
 */
const isCacheValid = (entry: CacheEntry<any>, cache_ttl: number = CACHE_TTL): boolean => {
  return Date.now() - entry.timestamp < cache_ttl;
};

/**
 * Clears all cached trees except for the site given
 */
const invalidateEquipmentTreeCache = (currentSite?: string): void => {
  for (let i = 0; i < localStorage.length; i++) {
    const keyName = localStorage.key(i);
    if (keyName?.startsWith(CACHE_EQUIPMENT_TREE_KEY_PREFIX)) {
      if (!currentSite || !keyName.endsWith(currentSite)) {
        localStorage.removeItem(keyName);
      }
    }
  }
};

/**
 * Equipment Service with methods for fetching equipment data
 */
export const equipmentService = {

  /**
   * Finds equipment by site and equipment prefix
   * @param site - Site identifier
   * @param equipmentPrefix - Equipment prefix to search for
   * @returns Promise resolving to array of equipment matches
   */
  async findEquipment(site: string, equipmentPrefix: string): Promise<Equipment[]> {
    const params = {
      site,
      eq_id: equipmentPrefix
    };
    const response = await apiClient.get<{ data: Equipment[] }>(
      '/equipment',
      { params }
    );
    return response.data;
  },

  /**
   * Retrieves the complete equipment tree for a site
   * @param site - Site identifier
   * @returns Promise resolving to complete equipment hierarchy
   */
  async getFullEquipmentTree(site: string): Promise<EquipmentHierarchy> {
    invalidateEquipmentTreeCache(site);
    const key = getFullTreeCacheKey(site);
    try {
      // Check if cached data exists and is valid
      const cachedItem = localStorage.getItem(key);
      if (cachedItem) {
        const cached: CacheEntry<EquipmentHierarchy> = JSON.parse(cachedItem);

        // Return cached data if still valid
        if (isCacheValid(cached, CACHE_TTL_EQUIPMENT_TREE)) {
          console.log(`Using cached data for ${site}`);
          return cached.data;
        }

        // Remove expired cache
        localStorage.removeItem(key);
      }
    } catch (error) {
      // If cache parsing fails, remove the corrupted entry
      localStorage.removeItem(key);
      console.warn(`Corrupted cache entry removed for ${site}:`, error);
    }
    try {
      const params = {
        site
      };
      const responseUrl = await apiClient.get<{ downloadUrl: string }>(
        '/equipment-tree-url',
        { params }
      );

      // Fetch fresh data
      console.log(`Fetching fresh data from ${responseUrl.downloadUrl}`);
      const response = await fetch(responseUrl.downloadUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: EquipmentHierarchy = await response.json();

      // Cache the data with expiration
      const cacheData: CacheEntry<EquipmentHierarchy> = {
        data,
        timestamp: Date.now(),
      };

      try {
        localStorage.setItem(key, JSON.stringify(cacheData));
      } catch (storageError) {
        // Handle localStorage quota exceeded or other storage errors
        console.warn(`Failed to cache data for ${site}:`, storageError);
      }

      return data;

    } catch (error) {
      console.error(`Failed to fetch data for ${site}:`, error);
      throw error;
    }
  },

  /**
   * Fetches equipment hierarchy for a specific site and APM ID
   * with caching for improved performance
   * @param site - Site identifier
   * @param apmId - APM identifier
   * @returns Promise resolving to equipment hierarchy
   */
  async getEquipmentHierarchy(site: string, apmId: string): Promise<EquipmentHierarchy> {
    const cacheKey = getCacheKey(site, apmId);

    // Check cache first
    const cachedData = equipmentCache[cacheKey];
    if (cachedData && isCacheValid(cachedData)) {
      return cachedData.data;
    }

    const params = {
      site,
      eq_id: encodeURIComponent(apmId)
    };
    // Fetch from API if not in cache or cache expired
    const data = await apiClient.get<EquipmentHierarchy>(
      '/equipment-tree',
      { params }
    );

    // Update cache
    equipmentCache[cacheKey] = {
      data,
      timestamp: Date.now()
    };

    return data;
  },

  /**
   * Fetches a specific equipment node by ID
   * @param equipmentId - Equipment identifier
   * @returns Promise resolving to equipment node
   */
  async getEquipmentById(equipmentId: string): Promise<EquipmentNode> {
    return apiClient.get<EquipmentNode>(
      `/api/equipment/${equipmentId}`,
    );
  },

  /**
   * Fetches children for a specific equipment node
   * @param equipmentId - Equipment identifier
   * @returns Promise resolving to array of child equipment nodes
   */
  async getEquipmentChildren(equipmentId: string): Promise<EquipmentNode[]> {
    return apiClient.get<EquipmentNode[]>(
      `/api/equipment/${equipmentId}/children`,
    );
  },

  /**
   * Clears the equipment cache for a specific site and APM ID
   * or the entire cache if no parameters are provided
   * @param site - Optional site identifier to filter cache entries
   * @param apmId - Optional APM identifier to filter cache entries
   */
  clearCache(site?: string, apmId?: string): void {
    if (site && apmId) {
      const cacheKey = getCacheKey(site, apmId);
      delete equipmentCache[cacheKey];
    } else {
      // Clear all cache
      Object.keys(equipmentCache).forEach(key => {
        delete equipmentCache[key];
      });
    }
  },

};